import { LineSeriesOption } from "echarts";
import { compact, isEmpty } from "lodash";
import dayjs from "dayjs";
import { getTimeSeriesTooltip } from "./getTimeSeriesTooltip";
import { FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { ExtendedActionPlanDetail } from "../../../../api/v2/getActionPlan";
import { Theme } from "@mui/material";

const calculateRefreshDateIndices = (refreshDates: Date[], allPeriodStarts: Date[]) => {
  if (isEmpty(refreshDates)) return [];
  return refreshDates
    .map((refreshDate) => {
      return allPeriodStarts.findIndex((periodStart) =>
        dayjs(periodStart).isSame(dayjs(refreshDate), "day")
      );
    })
    .filter((index) => index !== -1);
};

const CHART_LEGENDS = [
  ["Baseline Revenue Forecast", "Actual Revenue", "Revised Revenue Forecast"],
  ["Baseline Spend Projection", "Actual Spend", "Revised Spend Projection"],
];

const getSelectedChartLegends = (isActionPlanComplete: boolean, isActionPlanRefreshed: boolean) => {
  if (isActionPlanComplete) {
    return {
      "Baseline Revenue Forecast": false,
      "Baseline Spend Projection": false,
    };
  }
  if (isActionPlanRefreshed) {
    return {
      "Baseline Spend Projection": false,
      "Actual Spend": false,
      "Revised Spend Projection": false,
    };
  }
  return {};
};

const getChartOverrides = (
  theme: Theme,
  isActionPlanComplete: boolean,
  isActionPlanRefreshed: boolean
) => ({
  grid: {
    left: 8,
    right: 20,
    top: 40,
    bottom: 80,
    containLabel: true,
  },
  legend: CHART_LEGENDS.map((data, index) => ({
    orient: "horizontal",
    data,
    bottom: index === 0 ? 30 : 0,
    show: true,
    itemWidth: 40,
    lineStyle: {
      width: 1,
    },
    textStyle: {
      fontSize: 12,
      fontFamily: "Montserrat, sans-serif",
      color: theme.palette.mode === "dark" ? "#FFFFFF" : "#2E2E2E",
    },
    selected: getSelectedChartLegends(isActionPlanComplete, isActionPlanRefreshed),
  })),
});

type GetTimeSeriesChartOptions = {
  actionPlan: ExtendedActionPlanDetail;
  formatCurrency: FormatCurrencyFunction;
  theme: Theme;
};

export const getTimeSeriesChart = ({
  actionPlan,
  formatCurrency,
  theme,
}: GetTimeSeriesChartOptions) => {
  const {
    isActionPlanRefreshed,
    isActionPlanComplete,
    refreshDates,
    historicalSpendRevenueAttribution,
    futureSpendRevenueAttribution,
    baseScenarioSpendRevenueAttribution,
  } = actionPlan;

  // Collect and sort all unique period starts
  const uniquePeriodStartsSet = new Set(
    [
      ...compact(historicalSpendRevenueAttribution.map((entry) => entry.periodStart)),
      ...compact(baseScenarioSpendRevenueAttribution.map((entry) => entry.periodStart)),
      ...compact(futureSpendRevenueAttribution.map((entry) => entry.periodStart)),
    ].map((date) => dayjs(date).startOf("day").toISOString())
  );
  const sortedUniquePeriodStarts = Array.from(uniquePeriodStartsSet).sort(
    (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
  );

  // Helper function to align data arrays based on sorted unique period starts
  const alignDataByPeriodStart = <T extends { periodStart?: string | Date | null }>(
    data: T[]
  ): (T | null)[] => {
    return sortedUniquePeriodStarts.map((periodStart) => {
      const entry = data.find((e) =>
        dayjs(e.periodStart).startOf("day").isSame(dayjs(periodStart), "day")
      );
      return entry || null;
    });
  };

  // Create aligned arrays based on sorted unique period starts
  const alignedHistoricalData = alignDataByPeriodStart(historicalSpendRevenueAttribution);
  const alignedBaseScenarioData = alignDataByPeriodStart(baseScenarioSpendRevenueAttribution);
  const alignedFutureData = alignDataByPeriodStart(futureSpendRevenueAttribution);

  const allPeriodStarts = sortedUniquePeriodStarts.map((date) => new Date(date));

  // Calculate refresh date indices for vertical lines
  const refreshDateIndices = calculateRefreshDateIndices(refreshDates ?? [], allPeriodStarts);

  const axisLabels = !isEmpty(allPeriodStarts)
    ? allPeriodStarts.map((periodStart) => dayjs(new Date(periodStart)).format("DD MMM"))
    : [];

  const series: LineSeriesOption[] = [];

  if (!isEmpty(baseScenarioSpendRevenueAttribution)) {
    series.push({
      name: "Baseline Revenue Forecast",
      data: alignedBaseScenarioData.map((entry) => entry?.revenue ?? null),
      type: "line",
      color: theme.palette.mode === "dark" ? "#03BD4E" : "#09A856",
      symbol: "none",
      lineStyle: {
        type: "dashed",
      },
    });

    series.push({
      name: "Baseline Spend Projection",
      data: alignedBaseScenarioData.map((entry) => entry?.spend ?? null),
      type: "line",
      color: "#FC9429",
      symbol: "none",
      lineStyle: {
        type: "dashed",
      },
    });

    if (!isActionPlanRefreshed) {
      // Add confidence interval for baseline forecast
      const baselineLowerBounds = alignedBaseScenarioData.map(
        (entry) => entry?.revenueLowerBound ?? null
      );
      const baselineUpperBounds = alignedBaseScenarioData.map(
        (entry) => entry?.revenueUpperBound ?? null
      );

      const hasBaselineBounds =
        baselineLowerBounds.some((val) => val !== null) &&
        baselineUpperBounds.some((val) => val !== null);

      if (hasBaselineBounds) {
        // Create an invisible baseline at the lower bound
        // Use same name as parent series so legend toggle affects both
        series.push({
          name: "Baseline Revenue Forecast",
          data: baselineLowerBounds,
          type: "line",
          color: "transparent",
          lineStyle: {
            opacity: 0,
          },
          symbol: "none",
          showSymbol: false,
          areaStyle: {
            color: "transparent",
          },
          stack: "baseline-bounds",
        });

        // Calculate the difference between the upper and lower bound for each period
        const baselineBoundsData = baselineLowerBounds.map((lowerBound, index) => {
          const upperBound = baselineUpperBounds[index];
          if (lowerBound === null || upperBound === null) {
            return null;
          }
          return (upperBound || 0) - (lowerBound || 0);
        });

        // Create a transparent line for the shaded area
        // Use same name as parent series so legend toggle affects both
        series.push({
          name: "Baseline Revenue Forecast",
          data: baselineBoundsData,
          type: "line",
          color: "transparent",
          lineStyle: {
            opacity: 0,
          },
          symbol: "none",
          showSymbol: false,
          areaStyle: {
            color: "rgba(9, 168, 86, 0.1)", // #09A856 at 10% opacity
          },
          stack: "baseline-bounds",
        });
      }
    }
  }

  if (!isEmpty(historicalSpendRevenueAttribution)) {
    series.push({
      name: "Actual Revenue",
      data: alignedHistoricalData.map((entry) => entry?.revenue ?? null),
      type: "line",
      color: "#EC0089",
      symbol: "none",
    });
    series.push({
      name: "Actual Spend",
      data: alignedHistoricalData.map((entry) => entry?.spend ?? null),
      type: "line",
      color: theme.palette.mode === "dark" ? "#4C99D2" : "#0E3F68",
      symbol: "none",
    });
  }

  if (isActionPlanRefreshed && !isEmpty(futureSpendRevenueAttribution)) {
    series.push({
      name: "Revised Revenue Forecast",
      data: alignedFutureData.map((entry) => entry?.revenue ?? null),
      type: "line",
      color: "#EC0089",
      symbol: "none",
      lineStyle: {
        type: "dashed",
      },
    });

    series.push({
      name: "Revised Spend Projection",
      data: alignedFutureData.map((entry) => entry?.spend ?? null),
      type: "line",
      color: theme.palette.mode === "dark" ? "#4C99D2" : "#0E3F68",
      symbol: "none",
      lineStyle: {
        type: "dashed",
      },
    });

    /**
     * Add shaded region for the future revenue bounds. The required implementation here is to create
     * an invisible baseline at the lower bound and then stack the difference (upper - lower)
     * on top to create the visible shaded area.
     */

    // Create aligned bounds data from the aligned future data
    const alignedLowerBounds = alignedFutureData.map((entry) => entry?.revenueLowerBound ?? null);
    const alignedUpperBounds = alignedFutureData.map((entry) => entry?.revenueUpperBound ?? null);
    const hasBaselineBounds =
      alignedLowerBounds.some((val) => val !== null) &&
      alignedUpperBounds.some((val) => val !== null);

    if (hasBaselineBounds) {
      // Create an invisible baseline at the lower bound
      // Use same name as parent series so legend toggle affects both
      series.push({
        name: "Revised Revenue Forecast",
        data: alignedLowerBounds,
        type: "line",
        color: "transparent",
        lineStyle: {
          opacity: 0,
        },
        symbol: "none",
        showSymbol: false,
        areaStyle: {
          color: "transparent",
        },
        stack: "bounds",
      });

      // Calculate the difference between the upper and lower bound for each period
      const boundsData = alignedLowerBounds.map((lowerBound, index) => {
        const upperBound = alignedUpperBounds[index];
        if (lowerBound === null || upperBound === null) {
          return null;
        }
        return (upperBound || 0) - (lowerBound || 0);
      });

      // Create a transparent line for the shaded area
      // Use same name as parent series so legend toggle affects both
      series.push({
        name: "Revised Revenue Forecast",
        data: boundsData,
        type: "line",
        color: "transparent",
        lineStyle: {
          opacity: 0,
        },
        symbol: "none",
        showSymbol: false,
        areaStyle: {
          color: "rgba(236, 0, 137, 0.1)", // #EC0089 at 10% opacity
        },
        stack: "bounds",
      });
    }
  }

  // Add a dedicated series for refresh date vertical lines (independent of legend toggles)
  if (refreshDateIndices.length > 0) {
    series.push({
      name: "Refresh Dates", // Won't appear in legend
      type: "line",
      data: [], // No actual data points
      silent: true, // Ignore mouse events
      legendHoverLink: false, // Don't interact with legend
      markLine: {
        silent: true,
        symbol: "none",
        label: {
          show: false,
        },
        lineStyle: {
          type: "dashed",
          color: theme.palette.mode === "dark" ? "#EBEBEB" : "#6E6972",
        },
        data: refreshDateIndices.map((index) => ({
          xAxis: index,
        })),
      },
    });
  }

  return {
    timeSeriesChart: series,
    axisLabels,
    tooltipOptions: getTimeSeriesTooltip(theme, formatCurrency, refreshDateIndices),
    overrides: getChartOverrides(theme, isActionPlanComplete, isActionPlanRefreshed),
  };
};
